// CSV: leerlo y escribirlo bien — ADR-2026-09-08-el-agente-lee-y-escribe-planillas.
//
// "Partir por comas" no alcanza y es la fuente clásica de datos rotos: un producto que se
// llama "Remera negra, oversize" parte la fila en dos y desde ahí TODAS las columnas
// quedan corridas, sin que nada falle. Así que se parsea de verdad: comillas, comillas
// escapadas (""), saltos de línea DENTRO de una celda, y CRLF.
//
// Y el separador se detecta: Excel en español exporta con PUNTO Y COMA (porque la coma es
// el separador decimal), así que un `.csv` argentino abierto asumiendo comas se lee como
// una sola columna gigante.
//
// Todo puro.

/** Separadores que probamos, en orden de preferencia ante empate. */
const CANDIDATOS = [',', ';', '\t'] as const;
export type Separador = (typeof CANDIDATOS)[number];

/**
 * Adivina el separador contando cuántos aparecen FUERA de comillas en las primeras
 * líneas. Gana el que más aparece; ante empate, la coma.
 */
export function detectarSeparador(texto: string): Separador {
  const muestra = texto.slice(0, 64_000);
  const cuenta: Record<string, number> = { ',': 0, ';': 0, '\t': 0 };
  let enComillas = false;
  let lineas = 0;
  for (let i = 0; i < muestra.length && lineas < 20; i++) {
    const c = muestra[i];
    if (c === '"') {
      enComillas = !enComillas;
      continue;
    }
    if (enComillas) continue;
    if (c === '\n') lineas += 1;
    else if (c in cuenta) cuenta[c] += 1;
  }
  let mejor: Separador = ',';
  for (const s of CANDIDATOS) if (cuenta[s] > cuenta[mejor]) mejor = s;
  return mejor;
}

/**
 * CSV → filas. Nunca tira: un archivo mal cerrado (comilla sin par) devuelve lo que se
 * pudo leer, que es más útil que un error.
 */
export function parsearCsv(texto: string, separador?: Separador): string[][] {
  const sep = separador ?? detectarSeparador(texto);
  // El BOM de Excel se cuela como primer carácter del primer encabezado.
  const s = texto.replace(/^\uFEFF/, '');
  const filas: string[][] = [];
  let fila: string[] = [];
  let celda = '';
  let enComillas = false;
  let i = 0;
  const cerrarCelda = () => {
    fila.push(celda);
    celda = '';
  };
  const cerrarFila = () => {
    cerrarCelda();
    filas.push(fila);
    fila = [];
  };
  while (i < s.length) {
    const c = s[i];
    if (enComillas) {
      if (c === '"') {
        // `""` dentro de comillas es una comilla literal.
        if (s[i + 1] === '"') {
          celda += '"';
          i += 2;
          continue;
        }
        enComillas = false;
        i += 1;
        continue;
      }
      celda += c;
      i += 1;
      continue;
    }
    if (c === '"') {
      enComillas = true;
      i += 1;
      continue;
    }
    if (c === sep) {
      cerrarCelda();
      i += 1;
      continue;
    }
    if (c === '\r') {
      // CRLF y CR sueltos cuentan como UN salto.
      cerrarFila();
      i += s[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    if (c === '\n') {
      cerrarFila();
      i += 1;
      continue;
    }
    celda += c;
    i += 1;
  }
  // Lo que quedó en el buffer es la última fila, salvo que el archivo termine en salto.
  if (celda !== '' || fila.length > 0) cerrarFila();
  // Una última fila vacía (archivo terminado en salto) no es una fila.
  while (filas.length > 0 && filas[filas.length - 1].every((c) => c === '')) filas.pop();
  return filas;
}

/** Una celda necesita comillas si tiene el separador, comillas, o un salto de línea. */
const escapar = (valor: string, sep: string): string =>
  valor.includes(sep) || valor.includes('"') || /[\r\n]/.test(valor)
    ? `"${valor.replace(/"/g, '""')}"`
    : valor;

/**
 * Filas → CSV. Con BOM: sin él, Excel en Windows abre los acentos como `Ã³` — y el
 * usuario ve "Camión" escrito mal en un archivo que nosotros generamos.
 */
export function aCsv(
  filas: string[][],
  opciones: { separador?: Separador; bom?: boolean } = {},
): string {
  const sep = opciones.separador ?? ',';
  const cuerpo = filas
    .map((f) => f.map((c) => escapar(c ?? '', sep)).join(sep))
    .join('\r\n');
  return (opciones.bom === false ? '' : '\uFEFF') + cuerpo;
}
